const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const toSearchRecord = (search: unknown): Record<string, unknown> =>
  typeof search === 'object' && search !== null && !Array.isArray(search) ? (search as Record<string, unknown>) : {};

const getString = (search: Record<string, unknown>, key: string) => {
  const value = search[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const getUuid = (search: Record<string, unknown>, key: string) => {
  const value = getString(search, key);
  return value && UUID_PATTERN.test(value) ? value : undefined;
};

const getBoolean = (search: Record<string, unknown>, key: string) => {
  const value = search[key];
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return undefined;
};

const getNumber = (search: Record<string, unknown>, key: string) => {
  const value = search[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : undefined;
  }
  return undefined;
};

export type SignUpSearch = {
  description?: string;
  im_ref?: string;
  inviteId?: string;
  irclickid?: string;
  promo?: string;
  rdt_cid?: string;
  referrer?: string;
  twclid?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
  utm_source?: string;
  utm_term?: string;
};

export const validateSignUpSearch = (search: unknown): SignUpSearch => {
  const searchRecord = toSearchRecord(search);

  return {
    description: getString(searchRecord, 'description'),
    im_ref: getString(searchRecord, 'im_ref'),
    inviteId: getUuid(searchRecord, 'inviteId'),
    irclickid: getString(searchRecord, 'irclickid'),
    promo: getString(searchRecord, 'promo'),
    rdt_cid: getString(searchRecord, 'rdt_cid'),
    referrer: getUuid(searchRecord, 'referrer'),
    twclid: getString(searchRecord, 'twclid'),
    utm_campaign: getString(searchRecord, 'utm_campaign'),
    utm_content: getString(searchRecord, 'utm_content'),
    utm_medium: getString(searchRecord, 'utm_medium'),
    utm_source: getString(searchRecord, 'utm_source'),
    utm_term: getString(searchRecord, 'utm_term'),
  };
};

export type SignUpLearnMoreSearch = SignUpSearch & {
  slide?: number;
};

export const validateSignUpLearnMoreSearch = (search: unknown): SignUpLearnMoreSearch => {
  const searchRecord = toSearchRecord(search);
  const slide = getNumber(searchRecord, 'slide');

  return {
    ...validateSignUpSearch(searchRecord),
    slide: slide !== undefined && Number.isInteger(slide) && slide >= 1 ? slide : undefined,
  };
};

export type LoginSearch = {
  redirect?: string;
};

export const validateLoginSearch = (search: unknown): LoginSearch => {
  const searchRecord = toSearchRecord(search);

  return {
    redirect: getString(searchRecord, 'redirect'),
  };
};

export type AcceptInviteSearch = {
  promo?: string;
};

export const validateAcceptInviteSearch = (search: unknown): AcceptInviteSearch => {
  const searchRecord = toSearchRecord(search);

  return {
    promo: getString(searchRecord, 'promo'),
  };
};

export type ResetPasswordSearch = {
  email?: string;
};

export const validateResetPasswordSearch = (search: unknown): ResetPasswordSearch => {
  const searchRecord = toSearchRecord(search);

  return {
    email: getString(searchRecord, 'email'),
  };
};

export type SsoCallbackSearch = SignUpSearch & {
  mcpFlow?: boolean;
};

export const validateSsoCallbackSearch = (search: unknown): SsoCallbackSearch => {
  const searchRecord = toSearchRecord(search);

  return {
    ...validateSignUpSearch(searchRecord),
    mcpFlow: getBoolean(searchRecord, 'mcpFlow'),
  };
};

export type CreateDraftTaskSearch = {
  description?: string;
  example?: string;
  exampleType?: string;
  isFromSignUp?: boolean;
  letterRecommendationId?: string;
  letterRecommendationTaskSuggestionIdx?: number;
  name?: string;
  suggestionId?: string;
};

export const validateCreateDraftTaskSearch = (search: unknown): CreateDraftTaskSearch => {
  const searchRecord = toSearchRecord(search);

  return {
    description: getString(searchRecord, 'description'),
    example: getString(searchRecord, 'example'),
    exampleType: getString(searchRecord, 'exampleType'),
    isFromSignUp: getBoolean(searchRecord, 'isFromSignUp'),
    letterRecommendationId: getString(searchRecord, 'letterRecommendationId'),
    letterRecommendationTaskSuggestionIdx: getNumber(searchRecord, 'letterRecommendationTaskSuggestionIdx'),
    name: getString(searchRecord, 'name'),
    suggestionId: getString(searchRecord, 'suggestionId'),
  };
};
